/**
 * The shared y-domain: every chart that draws two series in one plot rectangle divides both
 * by one scale, stretches the result onto 0..1, and derives its axis ticks by mapping nice
 * round values back through the same pair. Kept free of component imports so it stays unit
 * testable — `helpers/Chart/index.ts` re-exports all three.
 */

/**
 * The divisor for a group's raw values. It cancels out of every plotted position — both
 * series in a group divide by it and the stretch renormalises on the group's own min/max —
 * so only its sign and its non-zeroness matter, and both of those matter a great deal:
 *
 * - a NEGATIVE scale (an entirely under-water series, e.g. outperformance) flips the plot
 *   upside down and hands the tick builder a reversed `[min, max]`, which then yields no
 *   ticks at all;
 * - a ZERO scale (a series peaking at exactly 0) divides every point to Infinity.
 *
 * Taking the magnitude leaves every positive series bit-for-bit unchanged.
 */
export const sharedDomainScale = (max: number) => Math.abs(max) || 1;

/** Map `value` onto the shared 0..1 plot domain, with 5% headroom above the top point. */
export const stretchYValueOnSharedDomain = (
  value: number,
  min: number,
  max: number,
) => (min === max ? value : 1 - (max - value) / ((max - min) * 1.05));

/**
 * Evenly spaced, round-number ticks across [min, max].
 * Picks a "nice" step (1/2/5 × 10ⁿ) so labels are clean full numbers and the
 * interval between ticks is constant. Returns the step too, so callers can
 * derive a matching decimal precision for the labels.
 */
export const niceLinearTicks = (
  min: number,
  max: number,
  targetTicks = 6,
): { values: number[]; step: number } => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
    return { values: [], step: 0 };
  }

  const rawStep = (max - min) / Math.max(1, targetTicks - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;

  let stepBase: number;
  if (normalized < 1.5) stepBase = 1;
  else if (normalized < 3) stepBase = 2;
  else if (normalized < 7) stepBase = 5;
  else stepBase = 10;

  const step = stepBase * magnitude;

  const values: number[] = [];
  const first = Math.ceil(min / step) * step;
  for (let v = first; v <= max + step * 1e-9; v += step) {
    values.push(parseFloat(v.toPrecision(12)));
  }

  return { values, step };
};
